import { useMemo } from 'react';
import { format, isValid } from 'date-fns';
import { defaultFormatStrings } from 'shared/utils/date';

type DefaultFormatStringsKeys = keyof typeof defaultFormatStrings;
type DefaultFormatStrings = (typeof defaultFormatStrings)[DefaultFormatStringsKeys];

type UseDateProps<T extends number | string | Date> = {
  date?: T;
  dateFormat?: DefaultFormatStrings;
  fallbackDate?: Date;
  fallbackString?: string;
  processDate?: (date: T) => Date;
};

/*
 * WARNING: be careful not to use this hook on its own returned date value.
 * It will add the timezone offset every time and we'll get incorrect dates.
 *
 * This hook is used to format dates coming in from the Subscription API (timestamps) in a consistent way across the application and account for timezone differnces.
 * date - if the date is not valid or does not exist, it will use the fallback date or to create the formatted string.
 * formattedDateString - if the date after parsing is invalid or does not exist it will fallback to
 *   the fallback string.
 * processDate() - a function to manipulate the date before starting the formatting process, e.g. add days to the date.
 *   The argument will be the date passed to the hook, return type must be of type Date.
 */
export function useDate<T extends number | string | Date>({
  date,
  dateFormat = 'MMM dd, yyyy',
  fallbackDate = new Date(),
  fallbackString = '',
  processDate
}: UseDateProps<T>) {
  const newDate = useMemo(
    () => (date && processDate ? processDate(date) : date),
    [date, processDate]
  );

  const parsedDate = useMemo(() => {
    if (!newDate) {
      return fallbackDate;
    }

    if (newDate instanceof Date) {
      if (!isValid(newDate)) {
        return fallbackDate;
      }

      return newDate;
    }

    const timestampAsNumber = Number(newDate);

    if (Number.isNaN(timestampAsNumber)) {
      return fallbackDate;
    }

    return new Date(timestampAsNumber * 1000);
  }, [fallbackDate, newDate]);

  const formattedDateString = useMemo(() => {
    if (!parsedDate || !isValid(parsedDate)) {
      return fallbackString;
    }

    return format(parsedDate, dateFormat);
  }, [dateFormat, fallbackString, parsedDate]);

  return {
    date: parsedDate,
    formattedDateString
  };
}
